import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { AlertTriangle, Truck, FileText, Clock, MapPin } from "react-feather";
import SectionCard from "../../../components/common/SectionCard";
import StatusBadge from "../../../components/common/StatusBadge";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import DispatchAlarmModal from "../components/DispatchAlarmModal";
import CauseChip from "../components/CauseChip";
import { useGetAlarmResponsesQuery, useGetMobileUnitsQuery } from "../alarmsApi";
import {
  ALARM_STATUS_CLASS, ALARM_STATUS_LABEL, INCIDENT_LABEL, formatTime, sinceDispatched, useCanDispatchAlarms,
} from "../alarmFormat";
import { paths } from "../../../routes/paths";

const REFRESH_MS = 30000;

// How long an alarm may sit unopened before its card turns red. A car that has
// not even opened the alert after this long needs a phone call.
const UNOPENED_ALERT_MINUTES = 3;

// Lanes, left to right in the order an alarm moves through them. REPORTED comes
// first because it is the only lane waiting on the control room itself.
const LANES = [
  { status: "REPORTED", title: "Waiting for you", hint: "The unit reported back - review and close or push to Tech" },
  { status: "DISPATCHED", title: "Not opened yet", hint: "Sent, the unit has not opened it" },
  { status: "OPENED", title: "On the way", hint: "Opened, travelling to site" },
  { status: "ARRIVED", title: "On site", hint: "Arrived, report not in yet" },
];

const AlarmCard = ({ r, now }) => {
  const stale =
    r.status === "DISPATCHED" && now - new Date(r.dispatchedDate).getTime() > UNOPENED_ALERT_MINUTES * 60000;
  return (
    <Link
      to={paths.alarmResponseDetail(r.recordGuid)}
      className="d-block text-reset text-decoration-none border rounded p-2 mb-2"
      style={{ background: stale ? "#FBE9E7" : "#fff", borderColor: stale ? "#C0392B" : undefined }}
    >
      <div className="d-flex align-items-center gap-2">
        <span className="fw-bold" style={{ fontSize: 13.5 }}>{r.customerName}</span>
        <span className="ms-auto small text-muted">{r.responseNumber}</span>
      </div>
      <div className="small">
        <span className="fw-semibold" style={{ color: "#C0392B" }}>{INCIDENT_LABEL[r.incidentType] || r.incidentType}</span>
        {" · "}
        <span className="text-muted">{r.incidentDescription}</span>
      </div>
      <div className="d-flex flex-wrap align-items-center gap-2 small mt-1">
        <span className="d-inline-flex align-items-center gap-1"><Truck size={12} /> {r.unitName}</span>
        {r.siteLabel && <span className="d-inline-flex align-items-center gap-1 text-muted"><MapPin size={12} /> {r.siteLabel}</span>}
        <span className={`ms-auto d-inline-flex align-items-center gap-1 ${stale ? "text-danger fw-bold" : "text-muted"}`}>
          <Clock size={12} /> {sinceDispatched(r.dispatchedDate, now)}
        </span>
        <CauseChip cause={r.alarmCause} size={10.5} />
      </div>
    </Link>
  );
};

const ControlRoomBoardPage = () => {
  const canDispatch = useCanDispatchAlarms();
  const { selectedBranchId } = useBranchFilter();
  const [showDispatch, setShowDispatch] = useState(false);
  const [now, setNow] = useState(Date.now());

  // Elapsed times tick every 15s without refetching.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  const branchParam = selectedBranchId != null ? { branchId: selectedBranchId } : {};
  const poll = { pollingInterval: REFRESH_MS };
  const { data: active = [], isLoading } = useGetAlarmResponsesQuery({ ...branchParam, activeOnly: true }, poll);
  const today = dayjs().format("YYYY-MM-DD");
  const { data: todayRows = [] } = useGetAlarmResponsesQuery({ ...branchParam, dateFrom: today, dateTo: today }, poll);
  const { data: units = [] } = useGetMobileUnitsQuery(branchParam, poll);

  const finishedToday = todayRows.filter((r) => ["CLOSED", "CONVERTED", "CANCELLED"].includes(r.status));
  const theftToday = todayRows.filter((r) => r.alarmCause === "THEFT_ATTEMPT").length;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Rapid Response - Control Room</h4>
              <h6>Live alarms and where every mobile unit is up to - refreshes every 30 seconds</h6>
            </div>
          </div>
          <div className="page-btn d-flex gap-2">
            <Link to={paths.alarmReport} className="btn btn-outline-secondary d-inline-flex align-items-center gap-1">
              <FileText size={15} /> Report
            </Link>
            {canDispatch && (
              <button type="button" className="btn btn-danger d-inline-flex align-items-center gap-1" onClick={() => setShowDispatch(true)}>
                <AlertTriangle size={15} /> New Alarm
              </button>
            )}
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <span className="badge badge-linedanger" style={{ fontSize: 13 }}>Active {active.length}</span>
          <span className="badge badge-linesuccess" style={{ fontSize: 13 }}>Finished today {finishedToday.length}</span>
          <span className="badge" style={{ fontSize: 13, background: "#C0392B", color: "#fff" }}>Theft today {theftToday}</span>
          <span className="badge badge-linesecondary" style={{ fontSize: 13 }}>Alarms today {todayRows.length}</span>
        </div>

        <SectionCard icon={Truck} title="Mobile units">
          {units.length === 0 ? (
            <p className="text-muted small mb-0">No mobile units yet - add them under Rapid Response &gt; Mobile Units.</p>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {units.map((u) => {
                const busy = !!u.activeResponseRecordGuid;
                const content = (
                  <>
                    <span className="fw-semibold">{u.unitName}</span>
                    <span className="small">
                      {busy ? `${u.activeResponseNumber} · ${ALARM_STATUS_LABEL[u.activeResponseStatus]}` : u.deviceUserId ? "Free" : "No device"}
                    </span>
                  </>
                );
                const style = {
                  background: busy ? "#FDF3E3" : u.deviceUserId ? "#E8F5EE" : "#F1F3F5",
                  border: `1px solid ${busy ? "#B26A00" : u.deviceUserId ? "#1E7B4A" : "#ADB5BD"}55`,
                  minWidth: 140,
                };
                return busy ? (
                  <Link key={u.recordGuid} to={paths.alarmResponseDetail(u.activeResponseRecordGuid)}
                    className="d-flex flex-column rounded px-3 py-2 text-reset text-decoration-none" style={style}>
                    {content}
                  </Link>
                ) : (
                  <div key={u.recordGuid} className="d-flex flex-column rounded px-3 py-2" style={style}>{content}</div>
                );
              })}
            </div>
          )}
        </SectionCard>

        {isLoading ? (
          <p className="text-muted">Loading...</p>
        ) : (
          <div className="row g-3">
            {LANES.map((lane) => {
              const rows = active.filter((r) => r.status === lane.status);
              return (
                <div key={lane.status} className="col-md-6 col-xl-3">
                  <div className="card h-100" style={{ background: "#FAFAFB" }}>
                    <div className="card-body p-2">
                      <div className="d-flex align-items-center gap-2 mb-1 px-1">
                        <StatusBadge status={lane.status} classMap={ALARM_STATUS_CLASS} labelMap={{ [lane.status]: lane.title }} />
                        <span className="ms-auto fw-bold">{rows.length}</span>
                      </div>
                      <div className="text-muted px-1 mb-2" style={{ fontSize: 11.5 }}>{lane.hint}</div>
                      {rows.length === 0 ? (
                        <div className="text-muted small px-1">Nothing here.</div>
                      ) : (
                        rows.map((r) => <AlarmCard key={r.recordGuid} r={r} now={now} />)
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <SectionCard icon={FileText} title={`Finished today (${finishedToday.length})`} className="mt-3">
          {finishedToday.length === 0 ? (
            <p className="text-muted small mb-0">Nothing finished yet today.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr><th>No.</th><th>Customer</th><th>Incident</th><th>Unit</th><th>Dispatched</th><th>Outcome</th><th>Cause</th></tr>
                </thead>
                <tbody>
                  {finishedToday.map((r) => (
                    <tr key={r.recordGuid}>
                      <td><Link to={paths.alarmResponseDetail(r.recordGuid)}>{r.responseNumber}</Link></td>
                      <td className="fw-semibold">{r.customerName}</td>
                      <td>{INCIDENT_LABEL[r.incidentType] || r.incidentType}</td>
                      <td>{r.unitName}</td>
                      <td>{formatTime(r.dispatchedDate)}</td>
                      <td>
                        <StatusBadge status={r.status} classMap={ALARM_STATUS_CLASS} labelMap={ALARM_STATUS_LABEL} />
                        {r.technicalWorkNumber && <span className="small ms-1">{r.technicalWorkNumber}</span>}
                      </td>
                      <td><CauseChip cause={r.alarmCause} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        <DispatchAlarmModal show={showDispatch} onClose={() => setShowDispatch(false)} />
      </div>
    </div>
  );
};

export default ControlRoomBoardPage;
